export const SECTION_CATEGORIES: Record<
  "aseos" | "mantenciones" | "productos" | "otros",
  string[]
> = {
  // Round 2026-06-05 — REDISEÑO del seccionado de /finanzas-cliente: las
  // pestañas dejan de partir los flujos por categoría. Ahora cada pestaña
  // refleja una FUENTE/módulo distinto (ver docs/flujo-financiero.md §10.5.1):
  //   • Aseos      → aseos de calendario (tabla `aseos`/Cleaning) vía el hook
  //                  `useAseosSection` (endpoint /finanzas/aseos/:id). YA NO
  //                  usa este mapeo de categorías.
  //   • Arriendos  → reservas.
  //   • Productos  → inventario.
  //   • Otros      → TODOS los flujos manuales de /flujos, sin importar la
  //                  categoría (lista vacía = sin filtro `categoria`; el
  //                  backend excluye los derivados de inventario, que van a
  //                  Productos).
  // Las claves `aseos` y `mantenciones` quedan acá solo por compat de tipos;
  // ninguna pestaña las usa ya (la de Reparaciones fue eliminada).
  aseos: ["aseo"],
  mantenciones: ["reparacion", "transporte", "emergencia"],
  productos: ["compra", "movimiento"],
  // [] = SIN filtro de categoría → trae TODOS los flujos de la propiedad.
  otros: [],
};

export type SectionKey = keyof typeof SECTION_CATEGORIES;

export type SectionId =
  | "arriendos"
  | "aseos"
  | "mantenciones"
  | "productos"
  | "otros";

export type SectionConfig = {
  id: SectionId;

  name: string;

  totalLabel: string;

  columnHelps?: Record<string, string>;
};

export const SECTION_CONFIG: Record<SectionId, SectionConfig> = {
  arriendos: {
    id: "arriendos",
    name: "Arriendos",
    totalLabel: "Tus ganancias",
    columnHelps: {
      ingreso: "Fecha ingreso del huésped (YYYY-MM-DD).",
      salida: "Fecha salida del huésped (YYYY-MM-DD).",
      noches: "Noches de estadía (salida - ingreso).",
      plataforma: "Origen de la reserva (Airbnb, Booking, etc.).",
      nombreHuesped: "Nombre del titular de la reserva.",
      huespedes: "Cantidad total de personas declaradas.",
      precioNoches: "Suma del precio de todas las noches",
      limpieza: "Tarifa de limpieza cobrada al huésped.",
      comisionPlataforma:
        "Comisión retenida por la plataforma (Airbnb/Booking).",
      extrasConComision: "Suma de extras con comisión de Aloja Fácil.",
      extrasSinComision: "Suma de extras sin comisión de Aloja Fácil.",
      descuentos: "Suma de descuentos aplicados a la reserva.",
      comisionAfNoches: "Comisión de Aloja Fácil por noches.",
      comisionAfExtras: "Comisión de Aloja Fácil por extras.",
      ivaAfNoches: "IVA sobre la comisión de Aloja Fácil por noches.",
      ivaAfExtras: "IVA sobre la comisión de Aloja Fácil por extras.",
      totalPropietario:
        "Liquidación final al propietario en CLP (después de limpieza, comisiones, IVA, extras y descuentos).",
    },
  },

  aseos: {
    id: "aseos",
    name: "Aseos",
    totalLabel: "Total de aseos",
  },

  mantenciones: {
    id: "mantenciones",
    name: "Mantenciones",
    totalLabel: "Total de mantenciones",
  },

  productos: {
    id: "productos",
    name: "Productos",
    totalLabel: "Total de productos",
  },

  otros: {
    id: "otros",
    name: "Otros",
    totalLabel: "Total otros gastos",
  },
};

export function getSectionConfig(id: SectionId): SectionConfig {
  const cfg = SECTION_CONFIG[id];
  if (cfg) return cfg;
  return { id, name: id, totalLabel: "Total" };
}

export function totalLabelFor(id: SectionId): string {
  return getSectionConfig(id).totalLabel;
}

export function columnHelpsFor(
  id: SectionId
): Record<string, string> | undefined {
  return getSectionConfig(id).columnHelps;
}

export type JsonbItem = {
  name?: string;
  _f_val?: number;
};

export type JsonbPair = { razon: string; monto: number };

export function normalizeJsonb(
  val: JsonbItem[] | null | undefined
): JsonbPair[] {
  if (!Array.isArray(val) || val.length === 0) return [];
  return val
    .map((it, idx) => ({
      razon: it.name ?? `Item ${idx + 1}`,
      monto: Number(it._f_val ?? 0),
    }))
    .filter((x) => Number.isFinite(x.monto));
}
